import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import MuiTextField from "../text-field/MuiTextField";
import CustomPagination from "./custom-pagination/PaginationCustom";
import compInfos from "./search-filter.json";
import { useLangContext } from "../../../context/langContext";
import MuiLinearProgress from "../../panel/common/MuiLinearProgress";
import { IFilterSchema, ITableSchema } from "../../../utils/types";
import FiltersContainer from "./filters/FiltersContainer";
import useUrlFilters from "../../../hooks/useUrlFilter";
import { ApplyUrlSortsFunction, Sorts } from "../../../hooks/useUrlSort";
import { SortDirectionEnum } from "../../../services/Schemas/ListQueryString";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";

const dataMsg = {
  fa: "داده ای جهت نمایش وجود ندارد.",
  en: "There is no data to display.",
};
interface ICustomTableProps {
  columns: ITableSchema[];
  rows: any[];
  count: number;
  handleChangePage: (page: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  page: number;
  rowsPerPage: number;
  hasTopPagination?: boolean;
  hasBottomPagination?: boolean;
  hasSearchFilter?: boolean;
  hasWaiting?: boolean;
  reloadAction?: () => void;
  removeAllAction?: () => void;
  onlyShowCount?: boolean;
  filtersSchema?: IFilterSchema[];
  onApplySorts?: ApplyUrlSortsFunction;
  sorts?: Sorts;
}

export default function CustomTable({
  columns,
  count,
  rows,
  handleChangePage,
  handleChangeRowsPerPage,
  page,
  rowsPerPage,
  hasBottomPagination,
  hasTopPagination,
  hasSearchFilter,
  hasWaiting,
  reloadAction,
  removeAllAction,
  onlyShowCount,
  filtersSchema,
  onApplySorts,
  sorts,
}: Readonly<ICustomTableProps>) {
  const totalPages = Math.ceil(count / rowsPerPage);
  const { lang } = useLangContext();
  const { parseUrlFilters, applyUrlFilters } = useUrlFilters();
  const filters = parseUrlFilters();
  const msg = dataMsg[lang];

  const handleSortClick = (column: string) => {
    if (!onApplySorts) return;
    const currentSort = sorts?.[column]?.direction;

    if (currentSort === SortDirectionEnum.ASC) {
      onApplySorts({
        ...sorts,
        [column]: { column, direction: SortDirectionEnum.DESC },
      });
    } else if (currentSort === SortDirectionEnum.DESC) {
      const { [column]: _, ...restSorts } = sorts || {};
      onApplySorts(restSorts);
    } else {
      onApplySorts({
        ...sorts,
        [column]: { column, direction: SortDirectionEnum.ASC },
      });
    }
  };

  return (
    <Paper
      sx={{
        width: "100%",
        minHeight: "fit-content",
        boxShadow: "none",
      }}
    >
      <Stack
        direction="row"
        justifyContent="end"
        alignItems="center"
        height="fit-content"
        py="10px"
        width="100%"
        px={{ xs: "4px", md: "24px" }}
        flexWrap={{ xs: "wrap", md: "nowrap" }}
      >
        <Stack direction="row" alignItems="center" mr="2px">
          {hasTopPagination && (
            <CustomPagination
              onlyShowCount={onlyShowCount}
              reloadAction={reloadAction}
              removeAllAction={removeAllAction}
              handlePage={handleChangePage}
              handlePageSizeChange={handleChangeRowsPerPage}
              dataLength={count}
              page={page}
              pageSize={rowsPerPage}
              totalPages={totalPages}
            />
          )}
        </Stack>
      </Stack>
      <Stack marginBottom={"16px"} px={{ xs: "4px", md: "24px" }}>
        {filtersSchema && filtersSchema.length > 0 && (
          <FiltersContainer
            onApplyFilters={applyUrlFilters}
            filtersSchema={filtersSchema}
            filters={filters}
          />
        )}
      </Stack>
      <Stack display={{ xs: "none", md: "flex", position: "relative" }}>
        {hasWaiting && <MuiLinearProgress sx={{ top: -5 }} />}
        <TableContainer sx={{ maxHeight: "fit-content" }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  const sortDirection = sorts?.[column.key]?.direction;

                  return (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      sx={{
                        background: "transparent",
                        py: "5px",
                        minWidth: column.minWidth,
                      }}
                    >
                      {column.sortable && onApplySorts ? (
                        <Button
                          onClick={() => handleSortClick(column.key)}
                          variant="text"
                          sx={{
                            margin:"auto",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            padding:0,
                            paddingRight:"4px",
                            textTransform: "none",
                            color: ({ palette }) => palette.dark[20],
                            background: "transparent",
                            transition: "background 0.2s ease",
                            "&:hover": {
                              background: ({ palette }) => palette.action.hover,
                            },
                            "&:active": {
                              background: ({ palette }) =>
                                palette.action.selected,
                            },
                          }}
                        >
                          <Typography variant="labelMediumBold">
                            {column.label}
                          </Typography>
                          <Stack
                            direction="column"
                            spacing={0}
                          >
                            <KeyboardArrowUp
                              fontSize="medium"
                              sx={{
                                opacity:
                                  sortDirection === SortDirectionEnum.ASC
                                    ? 1
                                    : 0.3,
                                color:
                                  sortDirection === SortDirectionEnum.ASC
                                    ? "primary.main"
                                    : "inherit",
                                transition: "0.2s",
                              }}
                            />
                            <KeyboardArrowDown
                              fontSize="medium"
                              sx={{
                                opacity:
                                  sortDirection === SortDirectionEnum.DESC
                                    ? 1
                                    : 0.3,
                                color:
                                  sortDirection === SortDirectionEnum.DESC
                                    ? "primary.main"
                                    : "inherit",
                                transition: "0.2s",
                              }}
                            />
                          </Stack>
                        </Button>
                      ) : (
                        <Typography
                          variant="labelMediumBold"
                          color={({ palette }) => palette.dark[20]}
                        >
                          {column.label}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length > 0 || hasWaiting ? (
                rows.map((row) => {
                  return (
                    <StyledTableRow key={row.code} hover>
                      {columns.map((column) => {
                        const value = row[column.key];
                        return (
                          <TableCell
                            key={column.key}
                            sx={{ py: "3px" }}
                            align={column.align}
                            height={"50px"}
                          >
                            <Typography
                              variant="labelMediumBold"
                              color={({ palette }) => palette.dark[40]}
                            >
                              {column.format ? column.format(value) : value}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </StyledTableRow>
                  );
                })
              ) : (
                <StyledTableRow>
                  <TableCell sx={{ py: "8px" }} colSpan={columns.length}>
                    <Stack
                      width="100%"
                      height="100px"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography
                        variant="h6DemiBold"
                        color={(theme) => theme.palette.dark[40]}
                      >
                        {msg}
                      </Typography>
                    </Stack>
                  </TableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <Stack display={{ xs: "flex", md: "none" }}>
        {rows.length > 0 || hasWaiting ? (
          rows.map((row) => (
            <Stack
              key={row.id}
              direction="column"
              py="8px"
              alignItems="start"
              justifyContent="start"
            >
              <Box
                width="100%"
                border="1px solid"
                my="8px"
                borderColor={({ palette }) => palette.gray[200]}
              />

              <Stack direction="column" width="100%" px="2px" gap="16px">
                {columns.map((column) => (
                  <Stack
                    key={column.key}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    gap="10px"
                  >
                    <Typography
                      color={({ palette }) => palette.dark[30]}
                      variant="labelMediumBold"
                    >
                      {column.label}
                    </Typography>
                    <Typography
                      color={({ palette }) => palette.dark[40]}
                      variant="labelMediumBold"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {column.format
                        ? column.format(row[column.key])
                        : row[column.key]}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          ))
        ) : (
          <Stack
            width="100%"
            height="100px"
            justifyContent="center"
            alignItems="center"
            bgcolor={(theme) => theme.palette.gray.bg}
          >
            <Typography
              variant="h6DemiBold"
              color={(theme) => theme.palette.dark[40]}
            >
              {msg}
            </Typography>
          </Stack>
        )}
      </Stack>

      {hasBottomPagination && rows.length > 0 && (
        <Stack
          px={{ xs: "4px", md: "24px" }}
          display={"flex"}
          flexDirection={"row"}
          justifyContent={{ xs: "start", md: "end" }}
        >
          <Stack width={"fit-content"}>
            <CustomPagination
              multiple
              handlePage={handleChangePage}
              handlePageSizeChange={handleChangeRowsPerPage}
              dataLength={count}
              page={page}
              pageSize={rowsPerPage}
              totalPages={totalPages}
            />
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
    "&:hover": {
      backgroundColor: theme.palette.gray[300],
    },
  },
  "&:nth-of-type(even)": {
    "&:hover": {
      backgroundColor: theme.palette.gray[300],
    },
  },

  // hide last border
  "&:last-child td, &:last-child th": {
    border: "none",
  },
  "&:not(:last-child)": {
    borderBottom: "none", // Remove bottom border
    border: "none",
    outline: "none",
    borderTop: "none",
  },
}));
